import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { HeroLook } from '@/components/hero/HeroOrb';
import { apiFetch } from '@/lib/api/client';
import { queryKeys } from '@/lib/queryKeys';

/**
 * 떠 있는 AI 오브의 색과 자리({@link AiAgentBubble} · 사용자 지시 2026-09-17).
 *
 * <h2>회사에 한 벌이다</h2>
 * 사람마다 다른 값이 아니라 <b>관리자가 정해 모두가 보는</b> 한 벌이다(서버
 * {@code AgentOrbDesignController}). 그래서 브라우저에 남기지 않는다 — 실험실의 색 시안은
 * {@code localStorage} 를 쓰지만 그쪽은 «내가 보려고 돌려 보는 자리» 고, 이쪽은 <b>확정</b>이다.
 *
 * <h2>색의 모양이 씬 타입과 같다</h2>
 * {@link OrbDesign} 의 {@code bg}·{@code orb}·{@code particleTint} 는 {@link HeroLook} 과 글자
 * 그대로 같은 모양이라 응답을 그대로 씬에 넘긴다({@link toLook} 은 그 셋을 떼어 줄 뿐이다).
 * 옮겨 담는 코드를 두면 색을 하나 늘릴 때마다 고칠 자리가 셋이 된다.
 */

export type OrbDesign = HeroLook & {
  /**
   * 오브 <b>가운데</b>가 창의 어디에 오는가 — 0‥1 의 비율이다.
   *
   * <p>픽셀이 아닌 이유: 1920 에서 끌어 둔 자리를 1366 노트북이 그대로 쓰면 화면 밖에 선다.
   * 화면 밖으로 나가지 않게 당기는 일은 그리는 쪽이 한다 — 창 크기를 아는 것은 거기뿐이다.
   */
  position: { xPct: number; yPct: number };
  /**
   * 구체 <b>머리 위의 이름표</b>. 비우면 이름표가 서지 않는다 — 여기서는 빈 값이 뜻을 가진다.
   *
   * <p>끄는 스위치를 따로 두지 않는 이유: 글자를 지우는 것이 곧 끄는 것이다. 스위치와 글자 칸이
   * 따로 있으면 «글자는 적혀 있는데 안 보이는» 상태가 생기고, 그게 왜인지 알 자리가 없다.
   */
  label: string | null;
  /*
    <b>무슨 말을 할지는 여기 없다</b>(사용자 지시 2026-09-18).

    <p>한동안 이 자리에 {@code lines} 가 있었다 — 관리자가 적어 둔 세 줄을 오브가 돌려 말했다.
    회사가 무엇을 적든 그 말은 <b>오늘 이 사람과 무관</b>했고, 세 시간마다 돌아오는 같은 문장은
    대화가 아니라 벽지였다. 지금은 홈 인사말의 신호 엔진이 사람마다 다른 말을 만든다
    ({@code orbLines.ts}).

    <p>남은 둘({@code sayDelaySec}·{@code sayEverySec})이 여기 그대로인 것은, <b>무엇을 말할지는
    사람마다 다르지만 얼마나 자주 말할지는 회사의 결정</b>이기 때문이다.
  */
  /**
   * 탭을 열고 <b>첫 말</b>까지(초). 들어오자마자 말하면 알림처럼 읽혀서 조금 두었다가 건넨다.
   *
   * <p>코드 상수가 아니라 설정인 이유: 3분에 한 번이 적당한지는 <b>돌려 봐야</b> 안다.
   * 상수로 두면 그때마다 배포해야 하고, 시험값을 되돌리는 것을 잊으면 전 직원이 그 값을 쓴다
   * (실제로 5초로 박아 둘 뻔했다 — 2026-09-17).
   */
  sayDelaySec: number;
  /** 그 뒤 <b>다시 말하기</b>까지의 간격(초). 아래 끝은 5초, 위 끝은 하루다. */
  sayEverySec: number;
};

/**
 * 서버가 아직 답하지 않았을 때 그리는 값. <b>서버 기본값과 같아야 한다</b>
 * ({@code AgentOrbDesign.defaults()}) — 다르면 응답이 오는 순간 오브의 색이 한 번 바뀐다.
 */
export const ORB_DESIGN_DEFAULT: OrbDesign = {
  bg: { color1: '#000000', color2: '#000000', color3: '#000000', color4: '#000000', color3Brightness: 0.88 },
  orb: { ambient: '#A00F16', clouds: '#A00F16', fresnel: '#F5E5E2', light: '#A00F16', light2: '#A00F16' },
  particleTint: '#A00F16',
  position: { xPct: 0.955, yPct: 0.93 },
  label: 'AI Agent',
  sayDelaySec: 20,
  sayEverySec: 180,
};

/** 말 간격의 아래·위 끝(초). 서버도 같은 값으로 당긴다({@code AgentOrbDesign.MIN_SAY_SEC}). */
export const SAY_SEC_RANGE = { min: 5, max: 24 * 60 * 60 } as const;

/** 씬에 넘길 색 한 벌만 떼어 낸다 — 자리는 씬이 알 바가 아니다. */
export function toLook(design: OrbDesign): HeroLook {
  return { bg: design.bg, orb: design.orb, particleTint: design.particleTint };
}

/**
 * 지금 색과 자리.
 *
 * <p><b>오래 들고 있는다</b>(`staleTime`). 이 값은 관리자가 몇 달에 한 번 만지는 것인데, 오브는
 * 모든 화면에 떠 있어 화면을 옮길 때마다 이 훅이 선다 — 기본값대로 두면 이동할 때마다 요청이 나간다.
 */
export function useOrbDesign() {
  return useQuery({
    queryKey: queryKeys.agentOrbDesign,
    queryFn: () => apiFetch<Partial<OrbDesign>>('/v1/agent-orb'),
    select: fillGaps,
    staleTime: 10 * 60 * 1000,
  });
}

/**
 * 응답에 <b>없는 칸</b>을 기본값으로 메운다.
 *
 * <p>이 값은 칸이 하나씩 늘어 왔다 — 색만 있다가 자리가 붙고, 이름표와 말이 붙었다. 그래서
 * <b>옛 서버(또는 아직 안 올라간 파드)의 응답</b>에는 새 칸이 통째로 없다. 그대로 쓰면 화면이
 * {@code undefined.length} 에서 터지거나, 이름표가 아무도 지운 적 없이 사라진다(2026-09-17).
 *
 * <p><b>{@code null} 은 메우지 않는다.</b> 그것은 「없음」 이 아니라 <b>「꺼 두었음」</b> 이다 —
 * 이름표를 지운 관리자의 뜻이라, 여기서 되살리면 지울 방법이 없어진다. 메우는 것은 키가 아예
 * 없을 때({@code undefined})뿐이다. 서버도 같은 규칙으로 읽는다({@code AgentOrbDesignService}).
 */
function fillGaps(raw: Partial<OrbDesign> | undefined): OrbDesign {
  return {
    bg: { ...ORB_DESIGN_DEFAULT.bg, ...raw?.bg },
    orb: { ...ORB_DESIGN_DEFAULT.orb, ...raw?.orb },
    particleTint: raw?.particleTint === undefined ? ORB_DESIGN_DEFAULT.particleTint : raw.particleTint,
    position: { ...ORB_DESIGN_DEFAULT.position, ...raw?.position },
    label: raw?.label === undefined ? ORB_DESIGN_DEFAULT.label : raw.label,
    sayDelaySec: raw?.sayDelaySec ?? ORB_DESIGN_DEFAULT.sayDelaySec,
    sayEverySec: raw?.sayEverySec ?? ORB_DESIGN_DEFAULT.sayEverySec,
  };
}

/**
 * 색 모달과 <b>끌어 놓기</b>가 같이 쓴다 — 한 벌을 통째로 보낸다.
 *
 * <p>응답을 캐시에 바로 심는다. 서버가 값을 좁혀서(범위 밖 자리를 당겨서) 돌려줄 수 있는데,
 * 다시 읽지 않으면 화면만 «보낸 값» 을 들고 있게 된다.
 */
export function useSaveOrbDesign() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (design: OrbDesign) => apiFetch<Partial<OrbDesign>>('/v1/agent-orb', { method: 'PUT', body: design }),
    // 캐시에는 <b>받은 그대로</b> 넣는다 — 읽는 쪽(`select`)이 빈 칸을 메우므로 여기서 또 메우면
    // 규칙이 두 곳이 된다.
    onSuccess: (saved) => queryClient.setQueryData(queryKeys.agentOrbDesign, saved),
  });
}
